import { useMutation } from '@tanstack/react-query'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { attachSurvey } from 'src/services/reservation-guests'
import { reservationApi } from 'src/services/reservations'

/**
 * Resolve the reservation reference the fill flow is scoped to. The reservation
 * may travel as a `?reservation=<id>` query param (the unified mechanism shared
 * with the guest self-fill link) or as the legacy `:id` route path segment.
 * The query param wins when both are present; existing path-segment entry
 * points keep working because they never set the query param.
 */
export const resolveReservationRef = (
  queryRef: string | null,
  pathId: string | undefined,
): string | undefined => queryRef || pathId || undefined

/**
 * What finishing the staff guest survey does, read from the route:
 * `?escalate=<reservationGuestId>` attaches the survey to that quick-added guest,
 * a `:guestId` segment updates that guest, otherwise a new guest is created.
 */
export function useGuestSurveyFinish() {
  const navigate = useNavigate()
  const { id: pathReservationId, guestId, formId } = useParams()
  const [searchParams] = useSearchParams()
  const reservationId = resolveReservationRef(searchParams.get('reservation'), pathReservationId)
  // Set when escalating a quick-added guest: the survey attaches to that row.
  const escalateGuestId = searchParams.get('escalate')
  const backToReservation = () => navigate(`/reservations/${reservationId}/`)

  const { mutate: escalateGuest, isPending: isEscalatingGuest } = useMutation({
    mutationFn: ({ guestId, instanceId }: { guestId: string; instanceId: string }) =>
      attachSurvey({ reservationGuestId: guestId, instanceId }),
    onSuccess: backToReservation,
  })

  const { mutate: createGuestFromSurvey, isPending: isCreatingGuest } = useMutation({
    mutationFn: ({ resId, instanceId }: { resId: string; instanceId: string }) =>
      reservationApi.csc.createGuest({ reservationId: resId, instanceId }),
    onSuccess: backToReservation,
  })

  const { mutate: updateGuestFromSurvey, isPending: isUpdatingGuest } = useMutation({
    mutationFn: ({
      resId,
      instanceId,
      guestId,
    }: {
      resId: string
      instanceId: string
      guestId: string
    }) => reservationApi.csc.updateGuest({ reservationId: resId, instanceId, guestId }),
    onSuccess: backToReservation,
  })

  const onFinish = () => {
    if (escalateGuestId) {
      escalateGuest({ guestId: escalateGuestId, instanceId: formId! })
    } else if (guestId) {
      updateGuestFromSurvey({ resId: reservationId!, instanceId: formId!, guestId })
    } else {
      createGuestFromSurvey({ resId: reservationId!, instanceId: formId! })
    }
  }

  return { onFinish, isPending: isCreatingGuest || isUpdatingGuest || isEscalatingGuest }
}
